import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  TrendingUp, TrendingDown, Users, Eye, UserPlus, Clock,
  ArrowUpRight, ArrowDownRight, BarChart3, Monitor, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// Tableau-inspired color palette
const COLORS = {
  blue: '#4e79a7',
  orange: '#f28e2b',
  red: '#e15759',
  teal: '#76b7b2',
  green: '#59a14f',
  yellow: '#edc948',
  purple: '#b07aa1',
  pink: '#ff9da7',
  brown: '#9c755f',
  gray: '#bab0ac',
};
const CHART_COLORS = Object.values(COLORS);
const GRADIENT_BLUE = ['#4e79a7', '#a8c8e8'];
const GRADIENT_GREEN = ['#59a14f', '#b5dfb0'];
const GRADIENT_ORANGE = ['#f28e2b', '#fdd49e'];

// Custom tooltip component
const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border shadow-lg px-4 py-3 text-sm" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
      <p className="font-semibold text-dark-slate mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-slate-gray">{entry.name}:</span>
          <span className="font-semibold text-dark-slate">
            {formatter ? formatter(entry.value, entry.name) : entry.value?.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

// KPI Card with trend indicator
const KPICard = ({ icon: Icon, label, value, trend, suffix = '', prefix = '', color = 'blue', sub }) => {
  const isPositive = trend > 0;
  const isZero = trend === 0;
  const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="rounded-2xl p-5 border shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS[color]}20` }}>
          <Icon className="w-5 h-5" style={{ color: COLORS[color] }} />
        </div>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-1 rounded-full ${
            isZero ? 'bg-slate-100 text-slate-gray' :
            isPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
          }`}>
            {!isZero && <TrendIcon className="w-3 h-3" />}
            {isZero ? '0%' : `${Math.abs(trend)}%`}
          </div>
        )}
      </div>
      <p className="text-3xl font-heading font-bold text-dark-slate">
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </p>
      <p className="text-sm text-slate-gray mt-1">{label}</p>
      {sub && <p className="text-xs text-slate-gray mt-0.5">{sub}</p>}
    </div>
  );
};

// Funnel visualization
const FunnelChart = ({ data }) => {
  if (!data) return null;
  const steps = [
    { label: 'Visitors', value: data.visitors, color: COLORS.blue },
    { label: 'Signups', value: data.signups, color: COLORS.green },
  ];
  const maxVal = Math.max(...steps.map(s => s.value), 1);

  return (
    <div className="space-y-3">
      {steps.map((step, i) => {
        const widthPct = Math.max((step.value / maxVal) * 100, 8);
        return (
          <div key={i}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-dark-slate">{step.label}</span>
              <span className="text-sm font-bold text-dark-slate">{step.value.toLocaleString()}</span>
            </div>
            <div className="w-full h-8 rounded-lg overflow-hidden" style={{ backgroundColor: 'hsl(var(--secondary))' }}>
              <div
                className="h-full rounded-lg transition-all duration-700 flex items-center justify-end pr-2"
                style={{ width: `${widthPct}%`, backgroundColor: step.color }}
              >
                {widthPct > 20 && (
                  <span className="text-xs font-semibold text-white">
                    {i > 0 ? `${data.conversion_rate}%` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div className="text-center mt-2">
        <span className="text-2xl font-heading font-bold" style={{ color: COLORS.green }}>
          {data.conversion_rate}%
        </span>
        <p className="text-xs text-slate-gray">Conversion Rate</p>
      </div>
    </div>
  );
};

// Format seconds to human readable
const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

// Format ms to readable
const formatMs = (ms) => {
  if (!ms || ms <= 0) return '0s';
  return formatDuration(ms / 1000);
};

const PERIODS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '12m', label: '12 Months' },
];

const AdminAnalyticsDashboard = () => {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await adminAPI.getDashboardAnalytics({ period: p });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load analytics dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(period);
  }, [period, fetchData]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-4" style={{ borderColor: COLORS.blue }}></div>
          <p className="text-sm text-slate-gray">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, traffic_trend, signup_trend, top_pages, page_dwell, session_duration_distribution, funnel } = data;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-dark-slate">Traffic & Engagement</h2>
          <p className="text-sm text-slate-gray">Performance overview for the selected period</p>
        </div>
        <div className="flex gap-1.5 p-1 rounded-xl" style={{ backgroundColor: 'hsl(var(--secondary))' }}>
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => handlePeriodChange(p.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                period === p.value
                  ? 'shadow-sm text-dark-slate'
                  : 'text-slate-gray hover:text-dark-slate'
              }`}
              style={period === p.value ? { backgroundColor: 'hsl(var(--card))' } : {}}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Eye}
          label="Visitors"
          value={kpis.visitors?.value || 0}
          trend={kpis.visitors?.trend}
          color="blue"
          sub="Unique sessions"
        />
        <KPICard
          icon={Monitor}
          label="Page Views"
          value={kpis.page_views?.value || 0}
          trend={kpis.page_views?.trend}
          color="orange"
          sub={`${kpis.pages_per_session || 0} pages/session`}
        />
        <KPICard
          icon={UserPlus}
          label="Signups"
          value={kpis.signups?.value || 0}
          trend={kpis.signups?.trend}
          color="green"
          sub={`${kpis.total_users?.toLocaleString() || 0} total users`}
        />
        <KPICard
          icon={Timer}
          label="Avg Session"
          value={formatDuration(kpis.avg_session_duration_s || 0)}
          color="teal"
          sub="Average time on site"
        />
      </div>

      {/* Traffic Trend Chart — Tableau style */}
      <div className="rounded-2xl p-6 border shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-lg text-dark-slate">Traffic Trend</h3>
            <p className="text-sm text-slate-gray">Daily visitors and page views</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.blue }} />
              <span className="text-slate-gray">Visitors</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.orange }} />
              <span className="text-slate-gray">Page Views</span>
            </div>
          </div>
        </div>
        {traffic_trend?.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={traffic_trend} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="gradVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.blue} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradPageViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.orange} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={COLORS.orange} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" />
              <YAxis tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="visitors" stroke={COLORS.blue} fill="url(#gradVisitors)" strokeWidth={2.5} name="Visitors" dot={false} activeDot={{ r: 5, stroke: COLORS.blue, strokeWidth: 2, fill: '#fff' }} />
              <Area type="monotone" dataKey="page_views" stroke={COLORS.orange} fill="url(#gradPageViews)" strokeWidth={2} name="Page Views" dot={false} activeDot={{ r: 5, stroke: COLORS.orange, strokeWidth: 2, fill: '#fff' }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-64 text-slate-gray text-sm">
            <div className="text-center">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 text-slate-200" />
              <p>Traffic data will appear as users visit the site</p>
            </div>
          </div>
        )}
      </div>

      {/* Signups Trend + Conversion Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Signup Trend */}
        <div className="lg:col-span-2 rounded-2xl p-6 border shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
          <h3 className="font-heading font-bold text-lg text-dark-slate mb-1">Signup Trend</h3>
          <p className="text-sm text-slate-gray mb-4">New user registrations over time</p>
          {signup_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={signup_trend} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="gradSignups" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.green} stopOpacity={0.9} />
                    <stop offset="95%" stopColor={COLORS.green} stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" interval={period === '7d' ? 0 : 4} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="signups" fill="url(#gradSignups)" radius={[4, 4, 0, 0]} name="Signups" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-52 text-slate-gray text-sm">No signup data yet</div>
          )}
        </div>

        {/* Conversion Funnel */}
        <div className="rounded-2xl p-6 border shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
          <h3 className="font-heading font-bold text-lg text-dark-slate mb-1">Conversion Funnel</h3>
          <p className="text-sm text-slate-gray mb-4">Visitor to signup rate</p>
          <FunnelChart data={funnel} />
        </div>
      </div>

      {/* Top Pages + Page Dwell Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="rounded-2xl p-6 border shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
          <div className="flex items-center gap-2 mb-4">
            <Monitor className="w-5 h-5" style={{ color: COLORS.blue }} />
            <h3 className="font-heading font-bold text-lg text-dark-slate">Top Pages</h3>
          </div>
          {top_pages?.length > 0 ? (
            <div className="space-y-2.5">
              {top_pages.map((pg, i) => {
                const maxViews = top_pages[0]?.views || 1;
                const pct = (pg.views / maxViews) * 100;
                return (
                  <div key={i} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs font-bold text-slate-gray w-5 shrink-0">{i + 1}</span>
                        <code className="text-sm text-dark-slate truncate">{pg.page}</code>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-3">
                        <span className="text-xs text-slate-gray">{pg.unique_visitors} unique</span>
                        <span className="text-sm font-bold text-dark-slate w-12 text-right">{pg.views.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'hsl(var(--secondary))' }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-gray text-sm">
              <Monitor className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Page view data will appear as users browse</p>
            </div>
          )}
        </div>

        {/* Page Dwell Time */}
        <div className="rounded-2xl p-6 border shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5" style={{ color: COLORS.teal }} />
            <h3 className="font-heading font-bold text-lg text-dark-slate">Time on Page</h3>
          </div>
          {page_dwell?.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.min(page_dwell.length * 36 + 20, 380)}>
              <BarChart data={page_dwell.slice(0, 10)} layout="vertical" margin={{ left: 80, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" tickFormatter={(v) => formatMs(v)} />
                <YAxis type="category" dataKey="page" tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" width={75} />
                <Tooltip content={<ChartTooltip formatter={(val) => formatMs(val)} />} />
                <Bar dataKey="avg_duration_ms" fill={COLORS.teal} radius={[0, 4, 4, 0]} name="Avg Time" barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-slate-gray text-sm">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Dwell time data will populate as users browse</p>
            </div>
          )}
        </div>
      </div>

      {/* Session Duration Distribution */}
      <div className="rounded-2xl p-6 border shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2 mb-4">
          <Timer className="w-5 h-5" style={{ color: COLORS.purple }} />
          <h3 className="font-heading font-bold text-lg text-dark-slate">Session Duration Distribution</h3>
          <span className="text-sm text-slate-gray ml-2">How long users stay per visit</span>
        </div>
        {session_duration_distribution?.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={session_duration_distribution} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" />
                <YAxis tick={{ fontSize: 11, fill: 'var(--slate-gray)' }} stroke="hsl(var(--border))" allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} name="Sessions">
                  {session_duration_distribution.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={session_duration_distribution.filter(d => d.count > 0)}
                  dataKey="count"
                  nameKey="bucket"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  label={({ bucket, percent }) => `${bucket} (${(percent * 100).toFixed(0)}%)`}
                >
                  {session_duration_distribution.filter(d => d.count > 0).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-gray text-sm">
            <Timer className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>Session data will populate as users browse the platform</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsDashboard;
